import type { ReactNode } from "react";

/**
 * Shared rendering primitives for long-form article bodies.
 *
 * Imported articles keep a small amount of inline Markdown (`**bold**` and
 * `[text](href)`) inside their text so tables and list items stay readable in
 * the content source. Blocks flagged with `md: true` — plus every table, list
 * and quote block — are rendered through `renderInline`.
 *
 * Legacy posts (plain text + a `links` array) are untouched: they never set
 * `md`, so their text still goes through `renderWithLinks`.
 */

export type ArticleTableBlock = { head: string[]; rows: string[][] };
export type ArticleListBlock = { items: string[]; ordered?: boolean };

const TOKEN = /\[([^\]]+)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*/g;

function isExternal(href: string) {
  return /^https?:\/\//i.test(href);
}

/** Render a string containing inline Markdown links and bold spans. */
export function renderInline(text: string): ReactNode {
  if (!text) return text;
  if (!text.includes("[") && !text.includes("**")) return text;

  const out: ReactNode[] = [];
  let cursor = 0;
  let m: RegExpExecArray | null;
  TOKEN.lastIndex = 0;
  let key = 0;

  while ((m = TOKEN.exec(text))) {
    if (m.index > cursor) out.push(text.slice(cursor, m.index));
    if (m[1] !== undefined && m[2] !== undefined) {
      const href = m[2];
      const label = m[1];
      out.push(
        isExternal(href) ? (
          <a
            key={`a${key++}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline-offset-4 hover:underline"
          >
            {renderInline(label)}
          </a>
        ) : (
          <a
            key={`a${key++}`}
            href={href}
            className="text-primary underline-offset-4 hover:underline"
          >
            {renderInline(label)}
          </a>
        ),
      );
    } else if (m[3] !== undefined) {
      out.push(
        <strong key={`b${key++}`} className="font-medium text-foreground">
          {m[3]}
        </strong>,
      );
    }
    cursor = m.index + m[0].length;
  }
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}

/** Strip inline Markdown for use in meta descriptions, schema and takeaways. */
export function stripInline(text: string): string {
  return text
    .replace(/\[([^\]]+)\]\([^)\s]+\)/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

export function ArticleTable({ head, rows }: ArticleTableBlock) {
  return (
    <div className="not-prose -mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
      <table className="w-full min-w-[34rem] border-collapse text-left text-sm">
        <thead>
          <tr className="border-b border-border">
            {head.map((h, i) => (
              <th
                key={i}
                scope="col"
                className="py-3 pr-4 align-bottom text-xs uppercase tracking-[0.14em] text-primary"
              >
                {renderInline(h)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-border/60 last:border-0">
              {r.map((c, j) => (
                <td key={j} className="py-3 pr-4 align-top leading-relaxed">
                  {renderInline(c)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ArticleList({ items, ordered }: ArticleListBlock) {
  const Tag = ordered ? "ol" : "ul";
  return (
    <Tag className={ordered ? "space-y-3 pl-1" : "space-y-3"}>
      {items.map((it, i) => (
        <li key={i} className="flex gap-3 leading-relaxed">
          <span aria-hidden="true" className="shrink-0 text-primary">
            {ordered ? `${i + 1}.` : "—"}
          </span>
          <span>{renderInline(it)}</span>
        </li>
      ))}
    </Tag>
  );
}

export function ArticleQuote({ children }: { children: ReactNode }) {
  return (
    <blockquote className="border-l-2 border-primary/50 pl-5 italic text-foreground/90">
      {children}
    </blockquote>
  );
}
